import { useState } from 'react';
import { useParams } from 'react-router-dom';
import useCaseFieldUpdate from './useCaseFieldUpdate';
import useDeleteConfirmation from './useDeleteConfirmation';
import { useAddCaseFormContext } from '../contexts/AddCaseFormContext';
import { DefaultMultiPlaintiffName } from '../utils/constant';
import { getDefaultClient } from '../utils/formSchema';
import { buildClientUpdatePayload, buildClientAddPayload } from '../utils/caseFieldPaths';
import { UPDATE_CASE_ACTION } from '@demandproai/shared';

const useCasePlaintiff = () => {
  const { formik, selectedTiffIndex, setSelectedTiffIndex } = useAddCaseFormContext();
  const { caseId } = useParams();
  const { updateCase, updateCaseById } = useCaseFieldUpdate({ caseId });

  // Modal state management
  const [showModal, setShowModal] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const openModal = (index) => {
    setSelectedIndex(index);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedIndex(null);
  };

  const updatePlainTiff = ({ clientObj, index }) => {
    const updated = [...formik.values.clients];
    updated[index] = clientObj;
    formik.setFieldValue('clients', updated);
    updateCaseById(buildClientUpdatePayload([clientObj]));
  };

  const addNewPlainTiff = (clientObj) => {
    const currentClients = formik.values.clients;
    const isReplacingDefault = (
      currentClients.length === 1 &&
      (currentClients[0]?.personalInfo?.clientName === DefaultMultiPlaintiffName.DEFAULT_PLAINTIFF_Name ||
       !currentClients[0]?.personalInfo?.clientName)
    );

    if (isReplacingDefault) {
      clientObj = {
        ...currentClients[0],
        personalInfo: clientObj?.personalInfo,
        insurance: clientObj?.insurance
      };
      formik.setFieldValue('clients', [clientObj]);
      updateCaseById(buildClientUpdatePayload([clientObj]));
    } else {
      // keep the new plaintiff's provider input mode aligned with the rest of the case
      const inheritedMode = currentClients[0]?.medicalProvidersInputMode;
      if (inheritedMode) {
        clientObj = { ...clientObj, medicalProvidersInputMode: inheritedMode };
      }
      formik.setFieldValue('clients', [...currentClients, clientObj]);
      updateCase({
        data: { clients: buildClientAddPayload(clientObj) },
        action: UPDATE_CASE_ACTION.ADD
      });
    }
  };

  const deletePlainTiff = (deletedIndex) => {
    const currentClients = formik.values.clients;
    const clientToDelete = currentClients[deletedIndex];

    if (currentClients.length === 1) {
      const resetClient = getDefaultClient();
      formik.setFieldValue('clients', [resetClient]);
      updateCase({
        data: { clients: { clientId: clientToDelete.clientId } },
        action: UPDATE_CASE_ACTION.REMOVE
      });
      updateCase({
        data: { clients: buildClientAddPayload(resetClient) },
        action: UPDATE_CASE_ACTION.ADD
      });
      return;
    }

    formik.setFieldValue('clients', currentClients.filter((_, index) => index !== deletedIndex));

    const newLength = currentClients.length - 1;
    if (deletedIndex < selectedTiffIndex) {
      setSelectedTiffIndex(selectedTiffIndex - 1);
    } else if (selectedTiffIndex >= newLength) {
      setSelectedTiffIndex(newLength - 1);
    }

    updateCase({
      data: { clients: { clientId: clientToDelete.clientId } },
      action: UPDATE_CASE_ACTION.REMOVE
    });
  };

  const deleteConfirm = useDeleteConfirmation({
    items: formik.values.clients,
    onDelete: deletePlainTiff,
  });

  return {
    updatePlainTiff,
    addNewPlainTiff,
    deletePlainTiff,
    requestDeletePlainTiff: deleteConfirm.request,
    confirmDeletePlainTiff: deleteConfirm.confirm,
    cancelDeletePlainTiff: deleteConfirm.cancel,
    pendingDeletePlaintiff: deleteConfirm.pendingItem,
    isConfirmingDelete: deleteConfirm.isConfirming,
    showModal,
    selectedIndex,
    openModal,
    closeModal,
  };
};

export default useCasePlaintiff;
